import { toNestErrors, validateFieldsNatively } from "@hookform/resolvers";
import { Validator, type OutputUnit, type Schema } from "@cfworker/json-schema";
import { appendErrors, type FieldError, type FieldValues, type Resolver } from "react-hook-form";

import {
  cleanupFormDataForSave,
  type DynamicFormSchema,
  type JsonFormObjectSchema,
  type JsonFormSchema,
} from "./form-schema";

const REQUIRED_PROPERTY_PATTERN = /required property "([^"]+)"/;

const getSchemaAtPath = (
  schema: JsonFormObjectSchema,
  path: string,
): JsonFormSchema | undefined => {
  const parts = path.split(".").filter(Boolean);
  let current: JsonFormSchema = schema;

  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      if (current.type !== "array" || !current.items) {
        return undefined;
      }

      current = current.items;
      continue;
    }

    if (current.type !== "object" || !current.properties?.[part]) {
      return undefined;
    }

    current = current.properties[part];
  }

  return current;
};

const getFieldErrorMessage = (
  schema: JsonFormObjectSchema,
  path: string,
  error: OutputUnit,
): string => {
  const fieldSchema = getSchemaAtPath(schema, path);
  const customMessage = fieldSchema?.["x-error-message"];

  if (customMessage) {
    return customMessage;
  }

  if (error.keyword === "required") {
    return "This field is required";
  }

  return error.error;
};

/** @cfworker/json-schema rejects explicit `undefined` values; react-hook-form emits them. */
const sanitizeForValidation = (
  value: unknown,
  isRoot = false,
  inArray = false,
): unknown => {
  if (value === undefined || value === "") {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeForValidation(entry, false, true))
      .filter((entry) => entry !== undefined);
  }

  if (value !== null && typeof value === "object") {
    const sanitizedEntries = Object.entries(value)
      .map(([key, entry]) => [key, sanitizeForValidation(entry)] as const)
      .filter(([, entry]) => entry !== undefined);

    if (sanitizedEntries.length === 0) {
      return isRoot || inArray ? {} : undefined;
    }

    return Object.fromEntries(sanitizedEntries);
  }

  return value;
};

const toFieldPath = (instanceLocation: string): string => {
  if (!instanceLocation || instanceLocation === "#") {
    return "";
  }

  return instanceLocation.replace(/^#\/?/, "").replace(/\//g, ".");
};

const getRequiredPropertyName = (error: OutputUnit): string | undefined => {
  const match = REQUIRED_PROPERTY_PATTERN.exec(error.error);

  return match?.[1];
};

const toErrorFieldPath = (error: OutputUnit): string => {
  const path = toFieldPath(error.instanceLocation);

  if (error.keyword === "required") {
    const propertyName = getRequiredPropertyName(error);

    if (!propertyName) {
      return path;
    }

    return path ? `${path}.${propertyName}` : propertyName;
  }

  return path;
};

const parseValidationErrors = (
  schema: JsonFormObjectSchema,
  validationErrors: OutputUnit[],
  validateAllFieldCriteria: boolean,
): Record<string, FieldError> =>
  validationErrors.reduce<Record<string, FieldError>>((previous, error) => {
    if (error.keyword === "if") {
      return previous;
    }

    const path = toErrorFieldPath(error);

    if (!path) {
      return previous;
    }

    const message = getFieldErrorMessage(schema, path, error);

    if (!previous[path]) {
      previous[path] = {
        message,
        type: error.keyword,
      };
    }

    if (validateAllFieldCriteria) {
      const types = previous[path].types;
      const messages = types && types[error.keyword];

      previous[path] = appendErrors(
        path,
        validateAllFieldCriteria,
        previous,
        error.keyword,
        messages ? ([] as string[]).concat(messages as string[], message) : message,
      ) as FieldError;
    }

    return previous;
  }, {});

const getRequiredEmptyArrayFieldErrors = (
  schema: JsonFormObjectSchema,
  values: FieldValues,
): Record<string, FieldError> => {
  const errors: Record<string, FieldError> = {};

  for (const fieldName of schema.required ?? []) {
    const field = schema.properties[fieldName];

    if (field?.type !== "array") {
      continue;
    }

    const value = values[fieldName];

    if (Array.isArray(value) && value.length === 0) {
      errors[fieldName] = {
        type: "required",
        message: field["x-error-message"] ?? "Select at least one option",
      };
    }
  }

  return errors;
};

export const createFormJsonSchemaResolver = (schema: DynamicFormSchema): Resolver => {
  const validator = new Validator(schema as Schema, "2020-12", false);

  return async (values, _, options) => {
    const sanitizedValues = sanitizeForValidation(values, true) as FieldValues;
    const cleanedValues = cleanupFormDataForSave(schema, sanitizedValues) as FieldValues;

    let result: { valid: boolean; errors: OutputUnit[] };

    try {
      result = validator.validate(cleanedValues);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Form validation failed.";

      return {
        values: {},
        errors: {
          root: {
            type: "validation",
            message,
          },
        } as Record<string, FieldError>,
      };
    }

    if (options.shouldUseNativeValidation) {
      validateFieldsNatively({}, options);
    }

    const validateAllFieldCriteria =
      !options.shouldUseNativeValidation && options.criteriaMode === "all";
    const fieldErrors = {
      ...parseValidationErrors(schema, result.errors, validateAllFieldCriteria),
      ...getRequiredEmptyArrayFieldErrors(schema, cleanedValues),
    };

    if (Object.keys(fieldErrors).length === 0 && result.valid) {
      return { values: cleanedValues, errors: {} };
    }

    if (Object.keys(fieldErrors).length === 0) {
      return {
        values: {},
        errors: {
          root: {
            type: "validation",
            message: "Form data does not match the schema.",
          },
        } as Record<string, FieldError>,
      };
    }

    return {
      values: {},
      errors: toNestErrors(fieldErrors, options),
    };
  };
};
